import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, MessageSquare, ExternalLink, ChevronDown } from 'lucide-react';

const BOT_CLIENT_ID = '1439517699003912223';
const BOT_PERMISSIONS = '2184703040';
const BOT_INVITE_URL = `https://discord.com/oauth2/authorize?client_id=${BOT_CLIENT_ID}&permissions=${BOT_PERMISSIONS}&scope=bot%20applications.commands`;

const CampaignCreatedModal = ({ campaign, onClose }) => {
  const shareableLink = `${window.location.origin}/join-campaign/${campaign.id}`;
  const [copiedField, setCopiedField] = useState(null);
  const [showDiscord, setShowDiscord] = useState(false);

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(field === 'link' ? 'Invite link copied!' : 'Campaign ID copied!');
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(field === 'link' ? 'Invite link copied!' : 'Campaign ID copied!');
      } catch (copyErr) {
        console.error('Failed to copy:', copyErr);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1E1E1E] border border-gray-700 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="text-center p-6 pb-4">
          <div className="w-14 h-14 bg-green-900/40 border border-green-700 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-100 font-serif">
            Campaign Created!
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            "{campaign.name}" is ready to go.
          </p>
        </div>

        <div className="px-6 pb-6 space-y-5">
          {/* Invite Link Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Share this link with your players:
            </label>
            <div className="flex">
              <input
                type="text"
                value={shareableLink}
                readOnly
                className="flex-1 px-3 py-2 bg-[#232323] border border-gray-600 rounded-l-md text-gray-100 text-sm font-mono"
              />
              <button
                onClick={() => copyToClipboard(shareableLink, 'link')}
                className="px-3 py-2 bg-red-900/40 border border-l-0 border-red-700 rounded-r-md text-red-300 hover:bg-red-900/60 transition-colors"
              >
                {copiedField === 'link' ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* What's Next Section */}
          <div className="border-t border-gray-700 pt-5">
            <h3 className="text-sm font-semibold text-gray-200 mb-3">What's Next</h3>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-900/50 border border-red-700 flex items-center justify-center text-xs text-red-300 mt-0.5">1</span>
                <p className="text-sm text-gray-300">Share the invite link above with your players</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-900/50 border border-red-700 flex items-center justify-center text-xs text-red-300 mt-0.5">2</span>
                <p className="text-sm text-gray-300">Check the <strong className="text-gray-200">Party</strong> tab to see who's joined</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-900/50 border border-red-700 flex items-center justify-center text-xs text-red-300 mt-0.5">3</span>
                <p className="text-sm text-gray-300">Write <strong className="text-gray-200">Session Notes</strong> during play to keep track of your story</p>
              </div>
            </div>
          </div>

          {/* Discord Bot Setup Section (Collapsible) */}
          <div className="border-t border-gray-700 pt-5">
            <button
              onClick={() => setShowDiscord(!showDiscord)}
              className="flex items-center gap-2 w-full text-left"
            >
              <MessageSquare className="w-5 h-5 text-purple-400" />
              <h3 className="text-sm font-semibold text-gray-200">Campaign Chronicler Setup</h3>
              <span className="text-xs text-gray-500">(Optional)</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${showDiscord ? 'rotate-180' : ''}`} />
            </button>
            <p className="text-xs text-gray-400 mt-2">
              Record and transcribe your sessions with our Discord bot. Get session summaries automatically.
            </p>

            {showDiscord && (
              <div className="space-y-3 mt-3">
                {/* Step 1: Invite Bot */}
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-900/50 border border-purple-700 flex items-center justify-center text-xs text-purple-300 mt-0.5">1</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-300">Invite the bot to your Discord server</p>
                    <a
                      href={BOT_INVITE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1.5 bg-purple-900/30 border border-purple-700 rounded text-sm text-purple-300 hover:bg-purple-900/50 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Invite Chaggerheart Bot
                    </a>
                  </div>
                </div>

                {/* Step 2: Link Campaign */}
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-900/50 border border-purple-700 flex items-center justify-center text-xs text-purple-300 mt-0.5">2</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-300">
                      Run this command in Discord to link your campaign:
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <code className="flex-1 px-2 py-1.5 bg-[#232323] border border-gray-600 rounded text-xs text-gray-200 font-mono">
                        /link {campaign.id}
                      </code>
                      <button
                        onClick={() => copyToClipboard(`/link ${campaign.id}`, 'id')}
                        className="px-2 py-1.5 text-gray-400 hover:text-gray-200 transition-colors"
                        title="Copy command"
                      >
                        {copiedField === 'id' ? (
                          <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 3: Record */}
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-900/50 border border-purple-700 flex items-center justify-center text-xs text-purple-300 mt-0.5">3</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-300">
                      Start recording with <code className="px-1 py-0.5 bg-gray-800 rounded text-xs">/record start</code> when your session begins
                    </p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3">
              You can configure this later in Campaign Settings.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => copyToClipboard(shareableLink, 'link')}
              className="flex-1 px-5 py-2 bg-red-900/40 text-red-300 rounded-md hover:bg-red-900/60 border border-red-700 transition-all duration-200 font-medium flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy Invite Link
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-5 py-2 bg-gray-800 text-gray-100 rounded-md hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700 transition-all duration-200 font-medium"
            >
              Go to Campaign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignCreatedModal;
